import React, { useEffect, useState, useRef } from "react";
import { Button, Modal, Form, Row, Col, Alert, Card, Badge } from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import { createReminder, updateReminder } from "../../afinanceapi";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./Comms.css";

function ReportEmailModal({ emailDetail, onSmsUpdate = () => {}, ...props }) {
  const [user, setUser] = useState({});
  const [successAlert, setSuccessAlert] = useState(false);
  const [closeAlert, setCloseAlert] = useState(false);

  const [emailData, setEmailData] = useState({
    subject: "",
    text: "",
    logos: {},
    ccSender: false,
    ccAddress: "",
  });

  const quillRef = useRef(null);

  const VARIABLES = [
    { label: "Patient Name", value: "${patientName}" },
    { label: "Session Date", value: "${sessionDate}" },
    { label: "Template Name", value: "${templateName}" },
    { label: "Practice Name", value: "${practiceName}" },
    { label: "Practice Phone", value: "${practicePhone}" },
    { label: "Practice Email", value: "${practiceEmail}" },
  ];

  useEffect(() => {
    const token = sessionStorage.getItem("user");
    if (!token) return;
    setUser(jwtDecode(token));
  }, []);

  useEffect(() => {
    if (emailDetail) {
      setEmailData({
        subject: emailDetail.reportSubject || emailDetail.report?.subject || "",
        text: emailDetail.reportText || emailDetail.report?.text || "",
        logos: emailDetail.reportLogos || emailDetail.report?.logos || {},
        ccSender: !!(emailDetail.reportCcSender ?? emailDetail.report?.ccSender),
        ccAddress: emailDetail.reportCcAddress ?? emailDetail.report?.ccAddress ?? "",
      });
    } else {
      setEmailData({ subject: "", text: "", logos: {}, ccSender: false, ccAddress: "" });
    }
  }, [emailDetail]);

  const updateField = (field, value) => {
    setEmailData((prev) => ({ ...prev, [field]: value }));
  };

  const insertIntoQuill = (value) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    quill.focus();
    const range = quill.getSelection();
    const index = range ? range.index : quill.getLength();
    quill.insertText(index, value, "user");
    quill.setSelection(index + value.length, 0);
    setTimeout(() => updateField("text", quill.root.innerHTML), 0);
  };

  const handleDropIntoQuill = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const varText = e.dataTransfer.getData("text/plain");
    if (varText) insertIntoQuill(varText);
  };
  const handleDragOverQuill = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const quillModules = {
    toolbar: [
      ["bold", "italic", "underline"],
      [{ align: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
    ],
  };
  const quillFormats = ["bold", "italic", "underline", "list", "bullet", "align", "link"];

  function extractPracticeNumbers(u) {
    const raw =
      (Array.isArray(u?.Practice) && u.Practice) ||
      (Array.isArray(u?.practice) && u.practice) ||
      (Array.isArray(u?.practiceNumbers) && u.practiceNumbers) ||
      (u?.practiceNumber ? [u.practiceNumber] : []);
    const mapped = raw.map((p) => {
      if (p && typeof p === "object") {
        return p.practiceNumber ?? p.practiceNo ?? p.practiceId ?? p.id ?? p._id ?? null;
      }
      return p ?? null;
    });
    return [...new Set(mapped.filter(Boolean))];
  }

  const normalizeHtml = (html) =>
    String(html).replace(/<p>/g, "<div>").replace(/<\/p>/g, "</div>");

  const handleSubmit = async (e) => {
    e.preventDefault();
    const practiceNumbers = extractPracticeNumbers(user);

    const baseExisting = emailDetail || {};
    const payload = {
      reminderType: "Email",
      // Preserve existing financial blocks if editing
      statementSubject: baseExisting.statement?.subject ?? baseExisting.statementSubject ?? "",
      statementText:    baseExisting.statement?.text    ?? baseExisting.statementText    ?? "",
      statementLogos:   baseExisting.statement?.logos   ?? baseExisting.statementLogos   ?? {},
      invoiceSubject:   baseExisting.invoice?.subject   ?? baseExisting.invoiceSubject   ?? "",
      invoiceText:      baseExisting.invoice?.text      ?? baseExisting.invoiceText      ?? "",
      invoiceLogos:     baseExisting.invoice?.logos     ?? baseExisting.invoiceLogos     ?? {},
      receiptSubject:   baseExisting.receipt?.subject   ?? baseExisting.receiptSubject   ?? "",
      receiptText:      baseExisting.receipt?.text      ?? baseExisting.receiptText      ?? "",
      receiptLogos:     baseExisting.receipt?.logos     ?? baseExisting.receiptLogos     ?? {},
      creditNoteSubject: baseExisting.creditNote?.subject ?? baseExisting.creditNoteSubject ?? "",
      creditNoteText:    baseExisting.creditNote?.text    ?? baseExisting.creditNoteText    ?? "",
      creditNoteLogos:   baseExisting.creditNote?.logos   ?? baseExisting.creditNoteLogos   ?? {},
      // Report block — what we're actually editing
      reportSubject: emailData.subject,
      reportText: normalizeHtml(emailData.text),
      reportLogos: emailData.logos,
      reportCcSender: !!emailData.ccSender,
      reportCcAddress: (emailData.ccAddress || "").trim(),
      practiceNumbers,
    };

    try {
      if (emailDetail?._id) await updateReminder(emailDetail._id, payload);
      else await createReminder(payload);
      setSuccessAlert(true);
      setTimeout(() => {
        setSuccessAlert(false);
        props.onHide();
        onSmsUpdate();
      }, 1000);
    } catch (error) {
      console.error("Error saving Report email template:", error);
    }
  };

  const handleClose = () => setCloseAlert(true);
  const handleCancelNo = () => setCloseAlert(false);
  const handleCancelYes = () => {
    setCloseAlert(false);
    props.onHide();
  };

  return (
    <Modal {...props} size="xl" centered onHide={handleClose} style={{ maxWidth: "95vw" }} scrollable>
      <Modal.Header closeButton={!closeAlert && !successAlert}>
        <Modal.Title className="text-primary">
          {emailDetail?.report?.text || emailDetail?.reportText
            ? "Edit Report Email Template"
            : "Add Report Email Template"}
        </Modal.Title>
      </Modal.Header>

      {closeAlert && (
        <Alert variant="info" className="w-100 d-flex align-items-center justify-content-between fs-6 fw-normal">
          <span>Are you sure you want to close? Changes will not be saved.</span>
          <div className="ms-auto d-flex">
            <Button variant="outline-primary" className="me-1" onClick={handleCancelYes}>
              <i className="fa fa-check"></i>
            </Button>
            <Button variant="outline-secondary" onClick={handleCancelNo}>
              <i className="fa fa-times"></i>
            </Button>
          </div>
        </Alert>
      )}

      {successAlert && (
        <Alert variant="success" className="w-100 d-flex align-items-center justify-content-between fs-6 fw-normal">
          <span>Report email template saved successfully!</span>
        </Alert>
      )}

      <Modal.Body style={{ maxHeight: "75vh", overflowY: "auto" }}>
        <Form className="mb-3">
          <Row>
            <Col lg={12}>
              <Card className="h-100">
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      id="reportCcSender"
                      label="CC sender (add the logged-in user's email to the CC when sending this report)"
                      checked={!!emailData.ccSender}
                      onChange={(e) => updateField("ccSender", e.target.checked)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="small mb-1">
                      Additional CC address(es)
                    </Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder="billing@example.com; partner@example.com"
                      value={emailData.ccAddress || ""}
                      onChange={(e) => updateField("ccAddress", e.target.value)}
                    />
                    <Form.Text className="text-muted">
                      Optional. Separate multiple addresses with <code>,</code> or <code>;</code>. Combines with the &quot;CC sender&quot; option above.
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>
                      <strong>Email Subject:</strong>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter subject for report emails"
                      value={emailData.subject}
                      onChange={(e) => updateField("subject", e.target.value)}
                    />
                  </Form.Group>

                  <VariablePills
                    availableVariables={VARIABLES}
                    tokenHelp="Use variables in the format ${variableName}"
                    onDragStart={(e, variable) => {
                      e.dataTransfer.setData("text/plain", variable.value);
                      e.dataTransfer.effectAllowed = "copy";
                    }}
                    onClick={(variable) => insertIntoQuill(variable.value)}
                  />

                  <Form.Group>
                    <Form.Label>
                      <strong>Email Body:</strong>
                    </Form.Label>
                    <div onDrop={handleDropIntoQuill} onDragOver={handleDragOverQuill}>
                      <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={emailData.text}
                        onChange={(value) => updateField("text", value)}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Enter your report email message. Drag a variable or click a pill to insert."
                        className="quill-editor"
                      />
                    </div>
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-center align-items-center w-100">
          <Button variant="primary" className="px-5 py-2" onClick={handleSubmit}>
            {emailDetail?._id ? "Save Changes" : "Create Report Email Template"}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default ReportEmailModal;

function VariablePills({ availableVariables, onDragStart, onClick, tokenHelp }) {
  return (
    <div className="mb-3">
      <small className="text-muted d-block mb-2">
        <strong>Available Variables:</strong> {tokenHelp}
      </small>
      <div className="d-flex flex-wrap gap-2">
        {(availableVariables || []).map((variable, idx) => (
          <Badge
            key={idx}
            bg="secondary"
            className="user-select-none"
            style={{ cursor: "grab", padding: "6px 10px", fontSize: "0.8rem" }}
            draggable
            onDragStart={(e) => onDragStart(e, variable)}
            onClick={() => onClick(variable)}
            title="Drag to editor or click to insert"
          >
            {variable.label}
          </Badge>
        ))}
      </div>
    </div>
  );
}
